import React, { useEffect, useState } from "react";
import { apiFetchJson } from "../api";
import { t, tf } from "../i18n";
import { A, aa, onAccent } from "../theme/palette";
import { ChevronDownIcon, MessageIcon, SparklesIcon, EditIcon } from "./Icons";

// Промты нейросети в настройках — у каждого пользователя свои.
//
// У каждого промта свой язык, RU или EN, — переключателем над полем. Смена
// языка подставляет в поле стандартный промт на этом языке; применяется он,
// как и любая правка, по «Сохранить». На языке промта сервер собирает и
// обвязку запроса, покупателю нейросеть всё равно пишет по-немецки.
//
// Сервер хранит язык и, если есть, свой текст пользователя; без своего текста
// действует стандартный, и в поле он стоит уже вставленным — его можно
// дописать или переписать целиком.
const PROMPTS = [
  {
    key: "assistant",
    icon: SparklesIcon,
    title: "Ассистент в диалогах",
    desc: "Как ассистент отвечает покупателям: тон, торг, доставка, оплата, резерв.",
    note: "Объявление, сегодняшняя дата и переписка добавляются к запросу сами — писать их сюда не нужно."
  },
  {
    key: "replySuggestion",
    icon: MessageIcon,
    title: "Быстрый ответ в поле ввода",
    desc: "Правила для готового ответа, который появляется серым текстом в поле ввода.",
    note: "Формат ответа нейросети сервер дописывает сам, его указывать не нужно. Подсказка включается в разделе «Нейросеть» выше."
  }
];

const PROMPT_LANG_OPTIONS = [
  { value: "ru", label: "RU" },
  { value: "en", label: "EN" }
];

const formatCount = (value) => Number(value || 0).toLocaleString("ru-RU");

// Ответ сервера — в состояние окна. onlyKey: после сохранения одного промта
// второй не трогаем, в нём может быть недописанная правка.
const mergePromptsResponse = (prev, data, onlyKey = "") => {
  const incoming = data?.prompts && typeof data.prompts === "object" ? data.prompts : {};
  const next = { ...prev };
  PROMPTS.forEach(({ key }) => {
    const entry = incoming[key];
    if (!entry) return;
    if (onlyKey && key !== onlyKey && prev[key]) return;
    const value = String(entry.value || "");
    const lang = entry.lang === "en" ? "en" : "ru";
    next[key] = {
      saved: value,
      savedLang: lang,
      draft: value,
      draftLang: lang,
      defaults: {
        ru: String(entry.defaults?.ru || ""),
        en: String(entry.defaults?.en || "")
      },
      custom: Boolean(entry.custom)
    };
  });
  return next;
};

const AiPromptsSettings = ({
  isDarkTheme = true,
  isPhoneView = false,
  surfaceTextPrimary,
  surfaceTextTertiary,
  surfaceInputText,
  onNotify
}) => {
  const [loadState, setLoadState] = useState({ status: "loading", error: "" });
  const [maxLength, setMaxLength] = useState(8000);
  // { [key]: { saved, savedLang, draft, draftLang, defaults: { ru, en }, custom } }
  const [prompts, setPrompts] = useState({});
  const [openKey, setOpenKey] = useState("");
  const [savingKey, setSavingKey] = useState("");

  const applyResponse = (data, onlyKey = "") => {
    if (Number(data?.maxLength) > 0) setMaxLength(Number(data.maxLength));
    setPrompts((prev) => mergePromptsResponse(prev, data, onlyKey));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetchJson("/api/settings/ai-prompts", { timeoutMs: 20000 });
        if (cancelled) return;
        if (Number(data?.maxLength) > 0) setMaxLength(Number(data.maxLength));
        setPrompts((prev) => mergePromptsResponse(prev, data));
        setLoadState({ status: "ready", error: "" });
      } catch (error) {
        if (!cancelled) setLoadState({ status: "error", error: error?.message || t("Не удалось загрузить промты") });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setDraft = (key, draft, draftLang) => {
    setPrompts((prev) => ({
      ...prev,
      [key]: { ...prev[key], draft, ...(draftLang ? { draftLang } : {}) }
    }));
  };

  // Другой язык — в поле стандартный промт на нём. Сохраняется по «Сохранить».
  const switchPromptLang = (key, nextLang) => {
    const entry = prompts[key];
    if (!entry || entry.draftLang === nextLang) return;
    setDraft(key, entry.defaults[nextLang] || "", nextLang);
  };

  const savePrompt = async (key) => {
    const entry = prompts[key];
    if (!entry) return;
    const text = String(entry.draft || "").trim();
    if (!text) {
      onNotify?.({ variant: "error", title: t("Промты нейросети"), body: t("Промт пустой. Чтобы вернуть стандартный, нажмите «Вернуть стандартный».") });
      return;
    }
    setSavingKey(key);
    try {
      const data = await apiFetchJson("/api/settings/ai-prompts", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: { text, lang: entry.draftLang } })
      });
      applyResponse(data, key);
      const custom = Boolean(data?.prompts?.[key]?.custom);
      onNotify?.({
        variant: "success",
        title: t("Промты нейросети"),
        body: custom ? t("Свой промт сохранён — нейросеть уже работает по нему.") : t("Действует стандартный промт.")
      });
    } catch (error) {
      onNotify?.({ variant: "error", title: t("Промты нейросети"), body: error?.message || t("Не удалось сохранить промт") });
    } finally {
      setSavingKey("");
    }
  };

  const cardBackground = isDarkTheme ? "rgba(23,25,33,0.4)" : "rgba(14,16,21,0.03)";
  const cardBorder = isDarkTheme ? "rgba(141,147,163,0.1)" : "rgba(14,16,21,0.08)";
  const ghostButtonStyle = {
    height: "36px",
    padding: "0 14px",
    borderRadius: "12px",
    border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.28)" : "rgba(14,16,21,0.14)"}`,
    background: "transparent",
    color: isDarkTheme ? "#c6c9d1" : "#262a33",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit"
  };

  return (
    <div style={{
      borderRadius: "0",
      border: "none",
      background: "transparent",
      boxShadow: "none",
      padding: isPhoneView ? "8px 2px 4px" : "10px 4px 6px"
    }}>
      <h3 style={{ color: surfaceTextPrimary, fontSize: "17px", fontWeight: "700", margin: "0 0 6px 0", display: "flex", alignItems: "center", gap: "10px" }}>
        <EditIcon size={18} color={A[400]} />
        {t("Промты нейросети")}
      </h3>
      <div style={{ color: surfaceTextTertiary, fontSize: "12.5px", lineHeight: 1.5, margin: "0 0 16px 0" }}>
        {t("Инструкции, по которым нейросеть пишет за вас. Стандартный текст уже вставлен — допишите его под себя или замените целиком. Промты свои у каждого пользователя.")}
      </div>

      {loadState.status === "loading" && (
        <div style={{ color: surfaceTextTertiary, fontSize: "13px", padding: "6px 2px" }}>{t("Загружаем промты…")}</div>
      )}
      {loadState.status === "error" && (
        <div style={{ color: "#f87171", fontSize: "13px", padding: "6px 2px" }}>{loadState.error}</div>
      )}

      {loadState.status === "ready" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {PROMPTS.map((item) => {
            const entry = prompts[item.key];
            if (!entry) return null;
            const Icon = item.icon;
            const isOpen = openKey === item.key;
            const draft = String(entry.draft || "");
            const draftLang = entry.draftLang === "en" ? "en" : "ru";
            const dirty = draft !== entry.saved || draftLang !== entry.savedLang;
            const isDefaultDraft = draft.trim() === String(entry.defaults[draftLang] || "").trim();
            const overLimit = draft.trim().length > maxLength;
            const saving = savingKey === item.key;
            const canSave = dirty && !saving && !overLimit && Boolean(draft.trim());
            return (
              <div
                key={item.key}
                className="ai-prompt-card"
                style={{
                  borderRadius: "18px",
                  background: cardBackground,
                  border: `1px solid ${isOpen ? aa(500, isDarkTheme ? 0.32 : 0.28) : cardBorder}`,
                  transition: "border-color 0.2s ease",
                  overflow: "hidden"
                }}
              >
                <button
                  type="button"
                  className="kl-plain-button"
                  onClick={() => setOpenKey(isOpen ? "" : item.key)}
                  aria-expanded={isOpen}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    background: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit"
                  }}
                >
                  <span style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "11px",
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: aa(500, isDarkTheme ? 0.14 : 0.1),
                    color: isDarkTheme ? A[300] : A[600]
                  }}>
                    <Icon size={16} color="currentColor" />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ color: surfaceInputText, fontSize: "14px", fontWeight: 600 }}>{t(item.title)}</span>
                      <span style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: "999px",
                        background: entry.custom ? aa(500, isDarkTheme ? 0.16 : 0.12) : (isDarkTheme ? "rgba(141,147,163,0.12)" : "rgba(14,16,21,0.06)"),
                        color: entry.custom ? (isDarkTheme ? A[300] : A[700]) : surfaceTextTertiary,
                        border: `1px solid ${entry.custom ? aa(500, 0.3) : (isDarkTheme ? "rgba(141,147,163,0.2)" : "rgba(14,16,21,0.1)")}`
                      }}>
                        {entry.custom ? t("Свой") : t("Стандартный")} · {entry.savedLang === "en" ? "EN" : "RU"}
                      </span>
                      {dirty && (
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#fbbf24" }}>
                          {t("● не сохранено")}
                        </span>
                      )}
                    </span>
                    <span style={{ display: "block", color: surfaceTextTertiary, fontSize: "12px", marginTop: "3px" }}>{t(item.desc)}</span>
                  </span>
                  <span style={{
                    color: surfaceTextTertiary,
                    display: "inline-flex",
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s ease",
                    flexShrink: 0
                  }}>
                    <ChevronDownIcon size={16} />
                  </span>
                </button>

                {isOpen && (
                  <div style={{ padding: isPhoneView ? "0 12px 14px" : "0 16px 16px" }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px 14px",
                      flexWrap: "wrap",
                      marginBottom: "10px"
                    }}>
                      <span style={{ color: surfaceTextTertiary, fontSize: "12px", lineHeight: 1.45, flex: "1 1 220px" }}>
                        {t("Язык промта. При смене в поле подставляется стандартный промт на этом языке — сохраните, чтобы применить.")}
                      </span>
                      <div
                        role="radiogroup"
                        aria-label={t("Язык промта")}
                        style={{
                          display: "inline-flex",
                          padding: "3px",
                          gap: "2px",
                          borderRadius: "12px",
                          background: isDarkTheme ? "rgba(8,9,12,0.55)" : "rgba(14,16,21,0.05)",
                          border: `1px solid ${isDarkTheme ? "rgba(141,147,163,0.2)" : "rgba(14,16,21,0.1)"}`,
                          flexShrink: 0
                        }}
                      >
                        {PROMPT_LANG_OPTIONS.map((option) => {
                          const active = draftLang === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              className="ai-prompt-lang-option"
                              onClick={() => switchPromptLang(item.key, option.value)}
                              disabled={saving}
                              style={{
                                minWidth: "46px",
                                height: "30px",
                                padding: "0 12px",
                                borderRadius: "9px",
                                border: "none",
                                background: active ? A[500] : "transparent",
                                color: active ? onAccent("#ffffff") : surfaceTextTertiary,
                                fontSize: "12px",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                fontFamily: "inherit",
                                cursor: active || saving ? "default" : "pointer",
                                transition: "background 0.2s ease, color 0.2s ease"
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <textarea
                      value={draft}
                      onChange={(event) => setDraft(item.key, event.target.value)}
                      spellCheck={false}
                      disabled={saving}
                      aria-label={t(item.title)}
                      style={{
                        width: "100%",
                        minHeight: isPhoneView ? "280px" : "340px",
                        resize: "vertical",
                        padding: "14px 16px",
                        borderRadius: "14px",
                        border: `1px solid ${overLimit ? "rgba(248,113,113,0.6)" : (isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.14)")}`,
                        background: isDarkTheme ? "rgba(8,9,12,0.55)" : "#ffffff",
                        color: surfaceInputText,
                        fontFamily: "inherit",
                        fontSize: "13px",
                        lineHeight: 1.6,
                        outline: "none",
                        boxSizing: "border-box",
                        transition: "border-color 0.2s ease"
                      }}
                      onFocus={(event) => { if (!overLimit) event.currentTarget.style.borderColor = aa(400, 0.6); }}
                      onBlur={(event) => {
                        event.currentTarget.style.borderColor = overLimit
                          ? "rgba(248,113,113,0.6)"
                          : (isDarkTheme ? "rgba(141,147,163,0.22)" : "rgba(14,16,21,0.14)");
                      }}
                    />
                    <div style={{ color: surfaceTextTertiary, fontSize: "12px", lineHeight: 1.5, marginTop: "8px" }}>
                      {t(item.note)}
                    </div>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      flexWrap: "wrap",
                      marginTop: "12px"
                    }}>
                      <span style={{ fontSize: "12px", color: overLimit ? "#f87171" : surfaceTextTertiary, fontVariantNumeric: "tabular-nums" }}>
                        {tf`${formatCount(draft.trim().length)} из ${formatCount(maxLength)} символов`}
                      </span>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginLeft: "auto", width: isPhoneView ? "100%" : "auto" }}>
                        {!isDefaultDraft && (
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => setDraft(item.key, entry.defaults[draftLang] || "")}
                            disabled={saving}
                            title={t("Подставить стандартный текст — сохранится, когда нажмёте «Сохранить»")}
                            style={{ ...ghostButtonStyle, flex: isPhoneView ? 1 : "none" }}
                          >
                            {t("Вернуть стандартный")}
                          </button>
                        )}
                        {dirty && (
                          <button
                            type="button"
                            className="kl-plain-button"
                            onClick={() => setDraft(item.key, entry.saved, entry.savedLang)}
                            disabled={saving}
                            style={{ ...ghostButtonStyle, flex: isPhoneView ? 1 : "none" }}
                          >
                            {t("Отменить")}
                          </button>
                        )}
                        {/* Без kl-plain-button: светлая тема красит такие кнопки
                            в серый, а эта должна остаться акцентной. */}
                        <button
                          type="button"
                          className="ai-prompt-save"
                          onClick={() => savePrompt(item.key)}
                          disabled={!canSave}
                          style={{
                            height: "36px",
                            padding: "0 18px",
                            borderRadius: "12px",
                            border: "none",
                            background: A[500],
                            color: onAccent("#ffffff"),
                            fontSize: "13px",
                            fontWeight: 600,
                            fontFamily: "inherit",
                            cursor: canSave ? "pointer" : "not-allowed",
                            opacity: canSave ? 1 : 0.45,
                            flex: isPhoneView ? "1 1 100%" : "none",
                            transition: "opacity 0.2s ease"
                          }}
                        >
                          {saving ? t("Сохраняем…") : t("Сохранить")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiPromptsSettings;
